import React, { useState, Fragment, useEffect } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import sanitizeHtml from 'sanitize-html';
import he from 'he';
import { get, keys, isEmpty, isNumber } from 'lodash-es';

import skeletons from './Skeletons';
import ShowMoreLess from '../../../../../components/ShowMoreLess';
import StarsBar from '../../../../../components/HorizontalBars/StarsBar';
import Tooltip from '../../../../../components/Tooltip';

import {
  numberToCurrency,
  numberToSize,
  numberToHumanSummary,
} from '../../../../../utils/formatter';

const clearText = (text) =>
  he.decode(sanitizeHtml(text).replace(/[^\w\s\!@#$%^&*()+={}\[\].,;:]/gi, ''));

const InsightsAppDetails = ({ details, settings }) => {
  const [data, setData] = useState({});

  const about_data = get(details, 'app_about.about_data');
  const other_data = get(details, 'app_about.other_data');
  const ratings = get(details, 'app_about.ratings');
  const filters = get(details, 'filters');

  useEffect(() => {
    setData({ ...about_data, ...other_data, ratings });
  }, [about_data, settings, ratings]);

  if (settings.pending) return skeletons.detailsAppDetails;

  const ratingTooltipContent = !isEmpty(ratings) && !isEmpty(ratings.ratings_breakdown) && (
    <div className="ratings-tooltip-breakdown">
      {[5, 4, 3, 2, 1].map((star) => (
        <div key={star} className="ratings-tooltip-breakdown-item">
          <StarsBar value={star * 0.2} size="md" />
          <span className="ratings-tooltip-breakdown-item-number">
            {numberToHumanSummary(ratings.ratings_breakdown[star])} ratings
          </span>
        </div>
      ))}
    </div>
  );

  const dataTypes =
    isEmpty(data) || isEmpty(ratings)
      ? {}
      : {
          ratings: {
            name: 'Ratings',
            body: (
              <div className="ratings-container">
                <div className="ratings-total">
                  <div className="ratings-total-stars">
                    <StarsBar value={ratings.avg_rating * 0.2} size="md" />
                  </div>
                  <div className="ratings-total-number">
                    {ratings.avg_rating}/5.0 ({numberToHumanSummary(ratings.total_ratings)} ratings)
                  </div>
                  <Tooltip
                    content={ratingTooltipContent}
                    colorVariant="light"
                    placement="right"
                    overlayClassName="ratings-tooltip"
                  >
                    <i className="hicon-question-circle cursor-pointer" />
                  </Tooltip>
                </div>
              </div>
            ),
          },
          price: {
            name: 'Price',
            body: (
              <div className="price-content">
                <span>{data.price && data.price > 0 ? numberToCurrency(data.price) : 'Free'}</span>
                <a
                  href={data.url}
                  className="insights-details-block__content-store-link link-default"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span
                    className={filters.store === 'google_play' ? 'hicon-play' : 'hicon-apple'}
                  />
                  {filters.store === 'google_play' ? (
                    <span>GooglePlay</span>
                  ) : (
                    <span>App Store</span>
                  )}
                </a>
              </div>
            ),
          },
          iap_present: {
            name: 'In-App Purchase',
            body: <span>{data.iap_present ? 'Yes' : 'No'}</span>,
          },
          ads_present: {
            name: filters.store !== 'itunes_connect' && 'Shows Ads',
            body: filters.store !== 'itunes_connect' && (
              <span>{data.ads_present ? 'Yes' : 'No'}</span>
            ),
          },
          released_date_formatted: {
            name: 'Launched',
            body: (
              <span>{data.released_on_dev_date_formatted || data.released_date_formatted}</span>
            ),
          },
          categories_list: {
            name: 'Store Categories',
            body: (
              <div className="store-categories-container">
                {(data.categories_list || []).map(
                  ({ category_name, category_id, is_performance_available }, i) => (
                    <Fragment key={category_id}>
                      {i > 0 && <span className="store-categories-divider">|</span>}
                      {is_performance_available && (
                        <a
                          className="link-default"
                          target="_blank"
                          rel="noreferrer"
                          href={`/categories/${filters.store}/${category_id}`}
                        >
                          {category_name}
                        </a>
                      )}
                      {!is_performance_available && <span>{category_name}</span>}
                    </Fragment>
                  )
                )}
              </div>
            ),
          },
          store_id: {
            name: 'Store ID',
            body: (
              <span>
                <a
                  className="link-default"
                  href={data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {data.store_id}
                </a>
              </span>
            ),
          },
          bundle_id: {
            name: 'Bundle ID',
            body: <span>{data.bundle_id}</span>,
          },
          file_size: {
            name: 'File Size',
            body: <span>{numberToSize(data.file_size)}</span>,
          },
          os_compatibility: {
            name: 'OS Compatibility',
            body: <span>{data.os_compatibility}</span>,
          },
          device_compatibility: {
            name: 'Device Compatibility',
            body: data.device_compatibility ? (
              <ShowMoreLess collapsedHeight={44} showMoreText="Show More" showLessText="Show Less">
                {data.device_compatibility.map(clearText).join(', ')}
              </ShowMoreLess>
            ) : (
              ''
            ),
          },
          age_rating: {
            name: 'Age Rating',
            body: <span>{data.age_rating}</span>,
          },
          has_widgets: {
            name: 'Widgets',
            body: <span>{data.has_widgets ? 'Yes' : 'No'}</span>,
          },
          has_app_clips: {
            name: 'App Clips',
            body: <span>{data.has_app_clips ? 'Yes' : 'No'}</span>,
          },
          has_apple_watch_app: {
            name: 'Apple Watch',
            body: <span>{data.has_apple_watch_app ? 'Yes' : 'No'}</span>,
          },
          privacy_url: {
            name: 'Privacy',
            body: (
              <span>
                <a
                  className="link-default"
                  href={data.privacy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Policy
                </a>
              </span>
            ),
          },
          languages_list: {
            name: 'Languages',
            body: data.languages_list ? (
              <ShowMoreLess collapsedHeight={44} showMoreText="Show More" showLessText="Show Less">
                {data.languages_list.join(', ')}
              </ShowMoreLess>
            ) : (
              ''
            ),
          },
          permissions: {
            name: 'Permissions',
            body: (
              <Fragment>
                <div className={cx('show-more')}>
                  <div className={cx('show-more-box')}>
                    {(data.permissions || []).map((item) => item.category).join(', ')}
                  </div>
                </div>
                {!!data.permissions && (
                  <ShowMoreLess
                    collapsedHeight={0}
                    toggleButtonPosition="top"
                    showMoreText="Show More"
                    showLessText="Show Less"
                  >
                    {data.permissions?.map((item) => (
                      <div key={item.category} className="permission">
                        <span className="permission-name">
                          {item.category}({item.permissions.length})
                        </span>
                        <span className="permission-description">
                          {item.permissions.map((permission) => (
                            <div key={permission.name} className="show-more mb-sm">
                              {permission.name}: {permission.description}
                            </div>
                          ))}
                        </span>
                      </div>
                    ))}
                  </ShowMoreLess>
                )}
              </Fragment>
            ),
          },
          interactive_elements: {
            name: 'Interactive Elements',
            body: (
              <span>
                <div className={cx('show-more')}>
                  <div className={cx('show-more-box')}>
                    {data.interactive_elements && data.interactive_elements.join(', ')}
                  </div>
                </div>
              </span>
            ),
          },
          app_products: {
            name: 'In-App Products',
            body: (
              <div className="app-products">
                {(data.app_products || []).map(({ name, price }, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="app-products-item">
                    <span className="app-products-item-name">
                      {filters.store === 'itunes_connect' && `${i + 1}.`} {name}
                    </span>
                    {price > 0 && (
                      <span className="app-products-item-price">
                        {numberToCurrency(price, { precision: 2 })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ),
          },
        };

  if (filters.store === 'google_play') {
    delete dataTypes.file_size;
  }

  return (
    <div className="insights-details-block">
      <h4 className="insights-details-block__head h4">App Details</h4>
      <div className="insights-details-block__content  insights-details-block__content-app-details">
        {keys(dataTypes).map(
          (key) =>
            (!isEmpty(data[key]) || isNumber(data[key])) && (
              <div key={key} className="insights-details-block__content-item">
                <span>{dataTypes[key].name}</span>
                <div>{dataTypes[key].body}</div>
              </div>
            )
        )}
      </div>
    </div>
  );
};

InsightsAppDetails.propTypes = {
  data: T.shape({
    store_id: T.oneOfType([T.string, T.number]),
    bundle_id: T.string,
    file_size: T.number,
    os_compatibility: T.string,
    device_compatibility: T.array,
    age_rating: T.string,
    has_widgets: T.bool,
    has_app_clips: T.bool,
    has_apple_watch_app: T.bool,
    privacy_url: T.string,
    languages_list: T.array,
    permissions: T.array,
    url: T.string,
    app_products: T.array,
    store: T.string.isRequired,
  }),
  details: T.object,
  settings: T.object,
};

export default InsightsAppDetails;
